import { CaretRightIcon, CheckIcon } from '@phosphor-icons/react';
import { Checkbox } from '@/components/ui/checkbox';
import { InitialsBadge } from './InitialsBadge';

export interface UserListRowProps {
  /** Display name of the user */
  name: string;
  /** Initials to show in the badge (usually 1-2 characters) */
  initials: string;
  /** Optional custom badge color class (defaults to auto-generated based on name) */
  badgeColorClass?: string;
  /** Optional additional classes for the container */
  className?: string;
  /** When true, displays a checkmark on the right side */
  isSelected?: boolean;
  /** Click handler - when provided, renders as an interactive button */
  onClick?: () => void;
  /** Optional timestamp string to display on the right (e.g., "Dec 12 | 9:21pm") */
  timestamp?: string;
  /** When true, displays a chevron icon on the right (overrides isSelected) */
  showChevron?: boolean;
  /** When true, displays a checkbox on the left side */
  showCheckbox?: boolean;
  /** Controls checkbox checked state */
  checkboxChecked?: boolean;
  /** Disables the checkbox input */
  checkboxDisabled?: boolean;
  /** Checkbox change handler */
  onCheckboxChange?: (checked: boolean) => void;
  /** When true, the checkbox renders where the initials badge normally appears */
  checkboxReplacesInitials?: boolean;
  /** Optional badge/pill to display before the name */
  badge?: React.ReactNode;
  /** Optional badge/pill to display on the right side before row actions */
  trailingBadge?: React.ReactNode;
  /** Optional content rendered under the name (e.g. role + group badges) */
  belowName?: React.ReactNode;
}

/**
 * A row displaying a user with an initial badge and name.
 * Used in attendance lists, RSVP lists, and member directories.
 * When onClick is provided, renders as an interactive button.
 */
export function UserListRow({
  name,
  className = '',
  isSelected,
  onClick,
  timestamp,
  showChevron,
  showCheckbox,
  checkboxChecked,
  checkboxDisabled,
  onCheckboxChange,
  checkboxReplacesInitials = false,
  badge,
  trailingBadge,
  belowName,
}: UserListRowProps) {
  const allowChevron = !showCheckbox;
  const showRowChevron = allowChevron && !!onClick;

  const rightContent = () => {
    if (showChevron) {
      if (timestamp) {
        return (
          <div className="flex items-center gap-2 text-slate-400">
            <span className="text-[clamp(12px,3vw,14px)]">{timestamp}</span>
            {allowChevron && (
              <CaretRightIcon
                size={18}
                weight="bold"
                className="text-slate-400"
              />
            )}
          </div>
        );
      }
      return allowChevron ? (
        <CaretRightIcon size={18} weight="bold" className="text-slate-400" />
      ) : null;
    }
    if (isSelected) {
      return <CheckIcon className="text-slate-50" />;
    }
    if (timestamp) {
      return (
        <div className="flex items-center gap-2 text-slate-400">
          <span className="text-[clamp(12px,3vw,14px)]">{timestamp}</span>
          {showRowChevron && (
            <CaretRightIcon
              size={18}
              weight="bold"
              className="text-slate-400"
            />
          )}
        </div>
      );
    }
    return null;
  };

  const content = (
    <>
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {showCheckbox && !checkboxReplacesInitials && (
          <span
            className="flex items-center flex-shrink-0"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
          >
            <Checkbox
              checked={checkboxChecked}
              disabled={checkboxDisabled}
              onCheckedChange={(checked) =>
                onCheckboxChange?.(checked === true)
              }
            />
          </span>
        )}
        <div className="flex-shrink-0">
          {showCheckbox && checkboxReplacesInitials ? (
            <span
              className="flex h-10 w-10 items-center justify-center"
              onPointerDown={(event) => event.stopPropagation()}
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
            >
              <Checkbox
                checked={checkboxChecked}
                disabled={checkboxDisabled}
                className="h-8 w-8 rounded-xl border-2 border-purple-300 data-[state=checked]:bg-purple-300 data-[state=checked]:text-slate-700"
                onCheckedChange={(checked) =>
                  onCheckboxChange?.(checked === true)
                }
              />
            </span>
          ) : (
            <InitialsBadge name={name} />
          )}
        </div>
        {badge && <div className="flex-shrink-0">{badge}</div>}
        <div className="min-w-0 flex-1 flex flex-col">
          <span
            className="text-large truncate min-w-0"
            style={{ fontSize: 'clamp(14px, 4vw, 18px)' }}
          >
            {name}
          </span>
          {belowName && <div className="mt-1 min-w-0">{belowName}</div>}
        </div>
      </div>
      {trailingBadge && <div className="shrink-0">{trailingBadge}</div>}
      {rightContent()}
    </>
  );

  if (onClick) {
    // When the row contains its own interactive content (e.g. a popover
    // trigger in belowName), render as a div with role="button" to avoid
    // nesting <button> elements.
    if (belowName) {
      return (
        <div
          role="button"
          tabIndex={0}
          onClick={onClick}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onClick();
            }
          }}
          className={`w-full flex items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer ${className}`}
        >
          {content}
        </div>
      );
    }
    return (
      <button
        type="button"
        onClick={onClick}
        className={`w-full flex items-center justify-between gap-4 px-6 py-5 text-left ${className}`}
      >
        {content}
      </button>
    );
  }

  return (
    <div
      className={`flex items-center justify-between gap-4 px-6 py-5 ${className}`}
    >
      {content}
    </div>
  );
}
